'use client';

import React, { forwardRef, useEffect, useState } from 'react';
import classnames from 'classnames';

import Box from '../../Box/Box';
import Typography from '../../Typography/Typography';
import Iconography, { IconographyProps } from '../../Iconography/Iconography';
import IconButton from '../../IconButton/IconButton';
// import { dataLayerHandler } from "../../../../utils/analytics";

import styles from './AlertItem.module.scss';

export type AlertItemType = 'neutral' | 'error' | 'informative' | 'success' | 'warning';
export type AlertItemVariant = 'solid' | 'outlined';
export type AlertItemTheme = 'dark' | 'light';
export type AlertItemPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center';
export type AlertItemDirection = 'ltr' | 'rtl';

export interface AlertItemProps extends React.HTMLAttributes<HTMLLIElement> {
  id?: string;
  theme?: AlertItemTheme;
  type?: AlertItemType;
  variant?: AlertItemVariant;
  position?: AlertItemPosition;
  title?: string;
  description?: string;
  iconLeft?: IconographyProps;
  offset?: number;
  gap?: number;
  direction?: AlertItemDirection;
  className?: string;
  onDismiss?: (id?: string) => void;
  dataTestId?: string;
  gaComponent?: string;
  gaText?: string;
  showDismiss?: boolean;
}

const AlertItem = forwardRef<HTMLLIElement, AlertItemProps>(
  (
    {
      id,
      theme = 'dark',
      type = 'neutral',
      variant = 'solid',
      position = 'bottom-right',
      title = '',
      description = '',
      iconLeft,
      offset = 0,
      gap = 0,
      direction = 'ltr',
      className,
      onDismiss,
      dataTestId = 'alertItem',
      gaComponent = 'alert',
      gaText = '',
      showDismiss = true,
      ...props
    },
    ref
  ) => {
    const colorMap = {
      neutral: {
        title: 'white',
        icon: theme === 'dark' ? 'white' : 'gray-9',
      },
      error: {
        title: 'error-1',
        icon: 'error-1',
      },
      informative: {
        title: 'information-1',
        icon: 'information-1',
      },
      success: {
        title: 'success-1',
        icon: 'success-1',
      },
      warning: {
        title: 'warning-1',
        icon: 'warning-1',
      },
    };

    const [verticalPosition, horizontalPosition] = position.split('-');
    const titleColor = colorMap[type]?.title;
    const iconColor = colorMap[type]?.icon;
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const [isDismissing, setIsDismissing] = useState<boolean>(false);

    useEffect(() => {
      setIsMounted(true); // Trigger the animation after mounting
      // Analytics tracking
      if (typeof window !== 'undefined') {
        const dataLayerMap: Record<string, unknown> = {
          event: 'interaction',
          interaction__type: 'alert open',
          interaction__component: gaComponent,
        };
        if (gaText) dataLayerMap.interaction__text = gaText;
        if (title || description) dataLayerMap.interaction__linkContext = title || description;
        (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
      }
    }, [id, gaComponent, gaText, title, description]);

    const handleClose = (alertId?: string) => {
      // Analytics tracking
      if (typeof window !== 'undefined') {
        const dataLayerMap: Record<string, unknown> = {
          event: 'interaction',
          interaction__type: 'alert close',
          interaction__component: gaComponent,
        };
        if (gaText) dataLayerMap.interaction__text = gaText;
        if (title || description) dataLayerMap.interaction__linkContext = title || description;
        (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
      }
      setIsDismissing(true); // Trigger the animation after dismiss
      onDismiss?.(alertId);
    };

    const renderLeftIcon = () => {
      if (!iconLeft) return null;
      return (
        <Iconography
          color={iconColor as IconographyProps['color']}
          className={styles.noShrink}
          {...iconLeft}
        />
      );
    };

    const renderCloseButton = () => {
      return (
        <Box className={styles.noShrink}>
          <IconButton
            buttonType="secondary"
            size="small"
            theme={theme}
            icon={{ category: 'global', name: 'close' }}
            handleClick={() => handleClose(id)}
            gaComponent={gaComponent}
            dataTestId={`${dataTestId}-closeButton`}
          />
        </Box>
      );
    };

    return (
      <li
        ref={ref}
        tabIndex={0}
        role="alert"
        data-testid={dataTestId}
        dir={direction}
        className={classnames(
          styles.alert,
          styles[verticalPosition],
          styles[horizontalPosition],
          styles[`${theme}_${variant}`],
          styles[type],
          {
            [styles.show]: isMounted,
            [styles.fadeOut]: isDismissing,
          },
          className
        )}
        // eslint-disable-next-line react/forbid-dom-props
        style={
          {
            '--alert-offset': `${offset}px`,
            '--alert-gap': `${gap}px`,
          } as React.CSSProperties
        }
        {...props}
      >
        <Box
          pt={{ xs: 12, xl: 16 }}
          pb={{ xs: 12, xl: 16 }}
          pl={{ xs: 12, xl: 16 }}
          pr={{ xs: 12, xl: 16 }}
          className={styles.copyContainer}
          direction={direction}
        >
          {renderLeftIcon()}
          <div>
            {title && (
              <Typography
                variant="body"
                size="medium"
                theme={theme}
                color={titleColor as React.ComponentProps<typeof Typography>['color']}
                direction={direction}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography
                variant="body"
                size="small"
                theme={theme}
                color="white"
                direction={direction}
              >
                {description}
              </Typography>
            )}
          </div>
        </Box>
        {showDismiss && renderCloseButton()}
      </li>
    );
  }
);

AlertItem.displayName = 'AlertItem';

export default AlertItem;
